import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';

import qs from 'querystring';
import { TextField, MenuItem } from '@material-ui/core';
import { useField } from '@unform/core';
import { isEmpty, isUndefined, isEqual } from 'lodash';
import PropTypes from 'prop-types';

import api from '~/services/api';
import { canURL } from '~/helpers';

import { Hidden } from '..';

function Combobox({
  unique,
  label,
  url,
  params,
  title,
  name,
  first,
  hiddens,
  getValue,
  disabled,
  ...rest
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fieldHidden, setFieldHidden] = useState(hiddens);
  const [oldParams, setOldParams] = useState({});

  const inputRef = useRef(null);
  const permission = isEqual(canURL(), { read: true });

  const {
    fieldName,
    defaultValue,
    registerField,
    error,
    clearError,
  } = useField(name);

  const hiddensArray = useMemo(
    () =>
      fieldHidden.map((h) => (
        <Hidden name={h.name} key={h.name} value={h.value || ''} />
      )),
    [fieldHidden]
  );

  function handleChangeValue(event) {
    const { value } = event.target;
    inputRef.current.value = value;

    getValue(
      data.find((dt) => !isUndefined(dt[unique]) && dt[unique] === value) || {}
    );

    // Check exists fields hidden
    if (!isEmpty(fieldHidden)) {
      const serachObject = data.find(
        (dt) => !isUndefined(dt[unique]) && dt[unique] === value
      );

      if (!isEmpty(serachObject)) {
        const objMod = hiddens.map((h) => ({
          ...h,
          value: serachObject[h.name],
        }));

        setFieldHidden(objMod);
      } else {
        setFieldHidden(hiddens);
      }
    }
  }

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [registerField, fieldName]);

  async function getData() {
    try {
      await setLoading(true);
      const response = await api.get(url, {
        params: { ...params, pagesize: 999999, pesquisalista: true },
        paramsSerializer: (param) => qs.stringify(param),
      });

      await setData(response.data.items);
      await setOldParams(params);
    } catch ({ erro }) {
      toast.error(erro);
    } finally {
      await setLoading(false);
    }
  }

  useEffect(() => {
    if (!isEqual(params, oldParams) && !loading) {
      getData();
    }
  }, [url, params]);

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (disabled) clearError();
  }, [disabled]);

  useEffect(() => {
    inputRef.current.value = defaultValue;
  }, [defaultValue]);

  useEffect(() => {
    getValue(
      data.find(
        (dt) => !isUndefined(dt[unique]) && dt[unique] === defaultValue
      ) || {}
    );
  }, [data, defaultValue, unique]);

  return (
    <>
      <TextField
        select
        displayEmpty
        label={title}
        ref={inputRef}
        onChange={handleChangeValue}
        error={!!error}
        defaultValue={defaultValue}
        helperText={error}
        variant="outlined"
        fullWidth
        size="small"
        InputLabelProps={{
          shrink: true,
        }}
        {...rest}
        disabled={permission || disabled}
        onFocus={clearError}
      >
        {first.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}

        {data.map((option) => (
          <MenuItem key={option[unique]} value={option[unique]}>
            {option[label]}
          </MenuItem>
        ))}
      </TextField>

      {hiddensArray}
    </>
  );
}

Combobox.propTypes = {
  unique: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  params: PropTypes.objectOf(PropTypes.object),
  title: PropTypes.string,
  name: PropTypes.string.isRequired,
  first: PropTypes.arrayOf(PropTypes.object),
  hiddens: PropTypes.arrayOf(PropTypes.string),
  getValue: PropTypes.func,
  disabled: PropTypes.bool,
};

Combobox.defaultProps = {
  params: {},
  title: 'Select',
  first: [],
  hiddens: [],
  getValue: () => {},
  disabled: false,
};

export default Combobox;
